import {
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  BTMenuResponse,
  BTModifierGroupItemResponse,
  BTModifierItemResponse,
  Button,
  CheckCircleIcon,
  CreateBTMenuModifierDto,
  Div,
  ErrorToast,
  H5,
  Icon,
  Modal,
  PlusIcon,
  PropsModal,
  SuccessToast,
  TextWithTooltipIcon,
  UpdateBTMenuModifierGroupDto,
  XCircleIcon,
  createBrandTemplateMenuModifierGroup,
  updateBrandTemplateMenuModifierGroup,
  reportError,
} from '@vgrubs/components';
import {
  ModifierType,
} from 'components/src/types/api/menu.types';


import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { Collapsible } from '../../MenyStyled';
import { ModifierBoxComponent } from './ModifierComponents';

const options2 = [
  { name: 'Price Group', value: ModifierType.Price },
  { name: 'Option Group', value: ModifierType.Option },
];

const typeOptions = (
  <optgroup label="Select Group Type">
    {options2.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const requiredOptions = [
  { name: 'No', value: 'false' },
  { name: 'Yes', value: 'true' },
];

const requiredOptionsGroup = (
  <optgroup label="Select Group Type">
    {requiredOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const choicesOptions = [
  { name: 'Only One', value: 'Only One' },
  { name: 'Set a min-max', value: 'Set a min-max' },
];

const choicesOptionsGroup = (
  <optgroup label="Select Group Type">
    {choicesOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

export const AddNewModifierGroupModal = ({
  addNewModal,
  closeModal,
  setMenu,
  setLoading,
  edit,
}: PropsModal & {
  addNewModal: boolean | BTModifierGroupItemResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
  edit?: boolean;
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [optionGroup, setOptionGroup] = useState<ModifierType>(
    options2[0].value as ModifierType
  );
  const [isRequired, setIsRequired] = useState(requiredOptions[0].value);
  const [choices, setChoices] = useState(choicesOptions[0].value);

  const [selectionsMin, setSelectionsMin] = useState<number | string>('');
  const [selectionsMax, setSelectionsMax] = useState<number | string>('');

  const [optionName, setOptionName] = useState('');
  const [fromPrice, setFromPrice] = useState(0);
  const [toPrice, setToPrice] = useState(0);

  const handleValueTotalChangeFromPrice = useCallback((val: number) => {
    setFromPrice(val);
  }, []);

  const handleValueTotalChangeToPrice = useCallback((val: number) => {
    setToPrice(val);
  }, []);

  const [options, setOptions] = useState<BTModifierItemResponse[]>([]);

  const ref = useRef<HTMLDivElement>(null);
  const elementHeight = ref?.current?.clientHeight;
  const [expanded, setExpanded] = useState(false);

  const [editingOption, setEditingOption] =
    useState<BTModifierItemResponse | null>(null);

  const { brandTemplateId } = useParams() as {
    brandTemplateId: string;
  };

  const required = isRequired === 'true';

  const data = addNewModal;

  useEffect(() => {
    if (edit && typeof data === 'object') {
      setName(data?.name || '');
      setDescription(data?.description || '');
      setOptionGroup(data?.type || ModifierType.Price);
      setIsRequired(data?.required ? 'true' : 'false');
      setOptions(data?.options || []);
      setSelectionsMin(data?.selectionsMin || '');
      setSelectionsMax(data?.selectionsMax || '');

      if (data?.required) {
        if (data?.selectionsMin === 1 && data?.selectionsMax === 1) {
          setChoices('Only One');
        } else {
          setChoices('Set a min-max');
        }
      } else {
        if (data?.selectionsMin === 0 && data?.selectionsMax === 1) {
          setChoices('Only One');
        } else {
          setChoices('Set a min-max');
        }
      }
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setName('');
    setDescription('');
    setOptionGroup(options2[0].value as ModifierType);
    setIsRequired(requiredOptions[0].value);
    setChoices(choicesOptions[0].value);
    setOptions([]);
    setSelectionsMin('');
    setSelectionsMax('');
  };

  async function handleAddModifierGroup() {
    if (!name) return ErrorToast('Please enter modifier group name');
    if (!description) return ErrorToast('Please enter modifier group notes');
    if (options.length < 1) return ErrorToast('Please add options');
    if (optionGroup === ModifierType.Option && selectionsMin > selectionsMax)
      return ErrorToast(
        'Maximum quantity must be grater then or equal to minimum'
      );

    // prettier-ignore
    if (optionGroup === ModifierType.Option && required && (selectionsMin !== null && Number(selectionsMin) < 1))
      return ErrorToast(
        `If it's required choice, Minimum Qty can't be les than 1`
      );

    const body: CreateBTMenuModifierDto = {
      name,
      description,
      type: optionGroup,
      options: options.map((o) => ({
        name: o.name,
        fromPrice: o.fromPrice,
        toPrice: o.toPrice,
      })),
      required,
      selectionsMin: Number(selectionsMin),
      selectionsMax: Number(selectionsMax),
    };

    const bodyEdit: UpdateBTMenuModifierGroupDto = {
      name,
      description,
      type: optionGroup,
      options: options.map((o) => ({
        name: o.name,
        id: o.id,
        fromPrice: o.fromPrice,
        toPrice: o.toPrice,
      })),
      required,
      selectionsMin: Number(selectionsMin),
      selectionsMax: Number(selectionsMax),
    };

    try {
      setLoading?.(true);
      const { data } =
        edit && typeof addNewModal === 'object' && addNewModal
          ? await updateBrandTemplateMenuModifierGroup(
              brandTemplateId,
              addNewModal.id,
              bodyEdit
            )
          : await createBrandTemplateMenuModifierGroup(brandTemplateId, body);

      emptyForm();
      setMenu(data?.data);
      SuccessToast(data?.message);
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  const handleAddOption = () => {
    if (!optionName) return ErrorToast('Please enter modifier name');
    setOptions((prev) => [
      ...prev,
      { name: optionName, fromPrice: fromPrice, toPrice: toPrice, id: 0 },
    ]);
    setOptionName('');
    setFromPrice(0);
    setToPrice(0);
    setExpanded(false);
  };

  const handleEditOption = () => {
    if (!optionName) return ErrorToast('Please enter modifier name');
    setOptions((prev) =>
      prev.map((o) =>
        o.id === editingOption?.id
          ? { ...o, name: optionName, fromPrice: fromPrice, toPrice: toPrice }
          : o
      )
    );
    setOptionName('');
    setFromPrice(0);
    setToPrice(0);
    setEditingOption(null);
  };

  const handleRemoveOption = (i: number) => {
    const arr = [...options];

    if (i > -1) {
      arr.splice(i, 1);
    }

    setOptions(arr);
  };

  const handleChangeIsRequired = (v: 'true' | 'false') => {
    const required = v === 'true';

    setIsRequired(v);
    if (required) {
      setSelectionsMin(1);
    } else {
      setSelectionsMin(0);
    }

    if (choices === 'Only One') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(1);
      }
    }
    if (choices === 'Set a min-max') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(0);
      }
    }
  };

  const handleChangeChoices = (v: string) => {
    setChoices(v);
    if (v === 'Only One') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(1);
      }
    }
    if (v === 'Set a min-max') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(0);
      }
    }
  };

  const handleChangeOptionGroup = (v: ModifierType) => {
    setOptionGroup(v);
    if (v === ModifierType.Price) {
      handleChangeIsRequired('true');
    }
    if (v === ModifierType.Option) {
      handleChangeIsRequired('false');
    }
  };

  const handleExpand = () => {
    setExpanded((prev) => !prev);
    handleValueTotalChangeFromPrice(0);
    handleValueTotalChangeToPrice(0);
    setOptionName('');
  };

  const handleEditExpand = (option: BTModifierItemResponse) => {
    setEditingOption(option);
    setOptionName(option.name);
    setFromPrice(option.fromPrice);
    setToPrice(option.toPrice);
  };

  const handleEditCancelExpand = () => {
    setEditingOption(null);
    setOptionName('');
    setFromPrice(0);
    setToPrice(0);
  };

  return (
    <Modal
      isOpen={!!addNewModal}
      closeModal={closeModal}
      header={`${edit ? 'Edit' : 'Add'} Modifier Group`}
      logo={false}
      zIndex={101}
      // width="850px"
    >
      <Div $flex $column $gap="24px">
        <Div $flex $column $alignCenter $gap="16px">
          <AdminInput
            placeholder="Enter Here"
            label="Group Name"
            $textCenter
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
          />
          <AdminSelect
            value={optionGroup}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) =>
              handleChangeOptionGroup(target.value as ModifierType)
            }
            label="Choose Group Type"
            $textCenter
            disabled={edit}
          />{' '}
        </Div>
        <AdminInput
          placeholder="Add context or internal names for this modifier group. Notes won't be."
          label="Notes"
          $textCenter
          maxLength={30}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          $textareaHeight={150}
        />

        <div>
          <Div
            $mt="8"
            $flex
            $between
            $alignCenter
            $gap="16px"
            $mb={options.length > 0 ? '16' : '0'}
          >
            <H5 $m="0">Modifiers</H5>

            {!expanded && (
              <Icon $pointer onClick={handleExpand}>
                <PlusIcon />
              </Icon>
            )}
          </Div>

          <Collapsible
            expanded={expanded || !!editingOption}
            elementHeight={elementHeight}
          >
            <div ref={ref}>
              <Div
                $box={expanded || !!editingOption}
                $p="24px 16px"
                $flex
                $column
                $alignCenter
                $gap="16px"
                $width="100%"
              >
                <AdminInput
                  placeholder="Enter Here"
                  label="Modifier Name"
                  $textCenter
                  value={optionName}
                  onChange={({ target }: { target: HTMLInputElement }) =>
                    setOptionName(target.value)
                  }
                />

                <Div $flex $column $gap="4px" $alignCenter>
                  <TextWithTooltipIcon
                    bold
                    text="Suggested Price From - To"
                    tooltip="The lower the price the higher the chances of people ordering."
                  ></TextWithTooltipIcon>

                  <Div $flex $alignCenter $gap="16px" $width="100%">
                    <AdminDollarInput2
                      $textCenter
                      onValueChange={handleValueTotalChangeFromPrice}
                      value={fromPrice}
                    />
                    <AdminDollarInput2
                      $textCenter
                      onValueChange={handleValueTotalChangeToPrice}
                      value={toPrice}
                    />
                  </Div>
                </Div>

                <Div $flex $gap="16px">
                  <Icon
                    $pointer
                    onClick={
                      editingOption ? handleEditCancelExpand : handleExpand
                    }
                  >
                    <XCircleIcon />
                  </Icon>
                  <Icon
                    $pointer
                    onClick={editingOption ? handleEditOption : handleAddOption}
                  >
                    <CheckCircleIcon />
                  </Icon>
                </Div>
              </Div>
            </div>
          </Collapsible>

          {options.length > 0 && (
            <Div $flex $column $gap="16px">
              {options.map((o, i) => {
                if (editingOption?.id === o.id) return null;
                return (
                  <ModifierBoxComponent
                    option={o}
                    key={i}
                    handleRemoveOption={handleRemoveOption}
                    handleEditOption={handleEditExpand}
                    i={i}
                  />
                );
              })}
            </Div>
          )}
        </div>

        {optionGroup === ModifierType.Option && (
          <>
            <AdminSelect
              value={isRequired}
              options={requiredOptionsGroup}
              onChange={({ target }: { target: HTMLSelectElement }) =>
                handleChangeIsRequired(target.value as 'true' | 'false')
              }
              label="Required Choice?"
              $textCenter
            />
            <AdminSelect
              value={choices}
              options={choicesOptionsGroup}
              onChange={({ target }: { target: HTMLSelectElement }) => {
                handleChangeChoices(target.value);
              }}
              label="How many choices can customer select?"
              $textCenter
            />
          </>
        )}

        {optionGroup === ModifierType.Option && choices === 'Set a min-max' && (
          <Div $flex $alignCenter $gap="16px" $width="100%">
            <AdminInput
              placeholder="Enter Here"
              label="Minimum Qty"
              $textCenter
              value={selectionsMin}
              onChange={({ target }: { target: HTMLInputElement }) =>
                setSelectionsMin(target.value)
              }
              type="number"
              min={required ? '1' : '2'}
            />
            <AdminInput
              placeholder="Enter Here"
              label="Maximum Qty"
              $textCenter
              value={selectionsMax}
              onChange={({ target }: { target: HTMLInputElement }) =>
                setSelectionsMax(target.value)
              }
              type="number"
            />
          </Div>
        )}
        <div>
          <Button $selected $w="100%" onClick={handleAddModifierGroup}>
            Confirm
          </Button>
          <Button
            $selected
            $mt="16"
            $gray
            $w="100%"
            onClick={() => closeModal?.()}
          >
            Cancel
          </Button>
        </div>
      </Div>
    </Modal>
  );
};
